import { MembershipStatusEnum } from '@sr-sdks/api-sdk-axios';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { queryOptionsGetMembership } from '../api/memberships/membershipApi';
import { queryOptionsGetWorkspace } from '../api/workspaces/workspaceApi';
import { WorkspaceOutletContext } from '../types/workspaceTypes';
import ViewModeField from './ViewModeField';

const getStatusDescription = (status: MembershipStatusEnum): null | string => {
  switch (status) {
    case MembershipStatusEnum.InReview:
      return 'Our team will review your workspace details and Membership Agreement for compliance (typical turnaround time: 1-2 business days).';

    case MembershipStatusEnum.PendingApproval:
      return 'Our team will review your workspace details and Membership Agreement for compliance (typical turnaround time: 1-2 business days).';

    default:
      return null;
  }
};

const TruncatedText = ({ text, title }: { text: string; title: string }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldTruncate = text.length > 300;
  const displayText =
    shouldTruncate && !isExpanded ? text.substring(0, 300) + '...' : text;

  return (
    <div>
      <ViewModeField
        title={title}
        value={
          <span>
            {displayText}
            {shouldTruncate && (
              <button
                className="ml-2 text-sm text-blue-600 hover:text-blue-800"
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? 'View less' : 'View more'}
              </button>
            )}
          </span>
        }
      />
    </div>
  );
};

interface MembershipFeedbackProps {
  isStaffView?: boolean;
}

const MembershipFeedback = ({ isStaffView }: MembershipFeedbackProps) => {
  const outletContext = useOutletContext<WorkspaceOutletContext | undefined>();
  const { data: workspace } = useQuery(
    queryOptionsGetWorkspace(outletContext?.workspaceId),
  );
  const { data: membership } = useQuery(
    queryOptionsGetMembership(workspace?.membershipId),
  );

  // Derive status similar to MembershipDetails to avoid stale state
  const staffViewStatus =
    membership?.membershipStatus || workspace?.membershipStatus || undefined;
  const nonStaffViewStatus =
    workspace?.membershipStatus || membership?.membershipStatus || undefined;
  const effectiveStatus = isStaffView ? staffViewStatus : nonStaffViewStatus;

  const description = effectiveStatus
    ? getStatusDescription(effectiveStatus)
    : null;
  const comment = membership?.comment;

  const hasComment = comment?.trim();
  const shouldShowComment =
    !!hasComment &&
    (effectiveStatus === MembershipStatusEnum.AwaitingUpdate ||
      effectiveStatus === MembershipStatusEnum.Rejected ||
      effectiveStatus === MembershipStatusEnum.ResignedMember ||
      effectiveStatus === MembershipStatusEnum.InactiveMember);

  if (
    (!description && !shouldShowComment) ||
    (isStaffView && !shouldShowComment)
  ) {
    return null;
  }

  return (
    <div className="space-y-4 text-sr-text-secondary">
      {/* Status description */}
      {description && !isStaffView && (
        <p className="pl-1 font-sans text-base font-normal leading-normal">
          {description}
        </p>
      )}

      {/* Comment feedback */}
      {shouldShowComment && (
        <TruncatedText title="Additional Information" text={comment || ''} />
      )}
    </div>
  );
};

export default MembershipFeedback;
